import { z } from 'zod';
import { apiClient, apiPost } from './client';
import { parseOrWarn } from '@/lib/zod-resolver';
import { ROLES } from '@/lib/role';

const roleSchema = z.enum(ROLES);

export const loginRequestSchema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
  /** 平台运营方登录时可选指定目标租户；普通登录不需要传 */
  tenant_code: z.string().optional(),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginResponseSchema = z.object({
  token: z.string().min(1),
  role: roleSchema,
  // R3-Sprint12-A UX:后端按 username 解析 tenant.slug,前端 setTenantCode 让
  // BrandingContext 立即 refetch(PLATFORM/无租户 → null)
  tenant_slug: z.string().nullable().optional(),
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const meResponseSchema = z.object({
  id: z.union([z.number(), z.string()]),
  username: z.string(),
  role: roleSchema,
  tenant_id: z.union([z.number(), z.string()]).nullable().optional(),
  display_name: z.string().optional(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export async function login(body: LoginRequest): Promise<LoginResponse> {
  const raw = await apiPost('/auth/login', body);
  return parseOrWarn(loginResponseSchema, raw, 'POST /auth/login');
}

// R3-Sprint12-B (Security)— 主动失效路径
export async function logout(): Promise<void> {
  // _silent:本调用即便失败,前端仍要清 localStorage 跳登录(不能因网络问题卡住用户登出)
  try {
    await apiClient.post('/auth/logout', undefined, { _silent: true });
  } catch {
    // 忽略 — 前端 useAuth.logout 仍清本地
  }
}

export const changePasswordRequestSchema = z.object({
  old_password: z.string().min(1),
  new_password: z.string().min(10, '至少 10 字符'),
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

export const changePasswordResponseSchema = z.object({
  token: z.string().min(1),
});
export type ChangePasswordResponse = z.infer<typeof changePasswordResponseSchema>;

export async function changePassword(body: ChangePasswordRequest): Promise<ChangePasswordResponse> {
  const raw = await apiPost('/auth/change-password', body);
  return parseOrWarn(changePasswordResponseSchema, raw, 'POST /auth/change-password');
}

export async function me(): Promise<MeResponse> {
  // TODO M3-β：后端 /auth/me 上线后切真接口。
  // 当前 v4 backend_app/app/api/v1/auth.py 仅有 /login；先从 localStorage 拼。
  const token = localStorage.getItem('token');
  const role = (localStorage.getItem('role') ?? 'staff') as MeResponse['role'];
  const username = localStorage.getItem('username') ?? 'demo';
  if (!token) throw new Error('not logged in');
  const stub: MeResponse = {
    id: 0,
    username,
    role,
    tenant_id: null,
    display_name: username,
  };
  return parseOrWarn(meResponseSchema, stub, 'stub /auth/me');
}
